'use client';

import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { cn } from '@/src/shared/lib/utils';
import type { OrderStatus } from '../types';

interface ChatItemProps {
  id: string;
  name: string;
  orderTitle?: string;
  orderStatus?: OrderStatus;
  lastMessage: string;
  time: string;
  avatar?: string;
  unreadCount?: number;
  isSelected?: boolean;
  onClick: (id: string) => void;
}

/**
 * Get status badge config based on order status
 */
function getStatusBadgeConfig(status: OrderStatus): {
  label: string;
  bgColor: string;
  textColor: string;
  icon?: 'claim' | 'done' | 'reviewed';
} | null {
  switch (status) {
    case 'ASSIGNED':
    case 'IN_PROGRESS':
      return {
        label: 'Claimed',
        bgColor: 'bg-blue-500',
        textColor: 'text-white',
        icon: 'claim',
      };
    case 'COMPLETED':
      return {
        label: 'Done',
        bgColor: 'bg-accent-secondary',
        textColor: 'text-white',
        icon: 'done',
      };
    case 'PENDING_REVIEW':
      return {
        label: 'Reviewed',
        bgColor: 'bg-accent-secondary',
        textColor: 'text-white',
        icon: 'reviewed',
      };
    default:
      return null;
  }
}

/**
 * Status badge icon component
 */
function StatusIcon({ icon }: { icon: 'claim' | 'done' | 'reviewed' }) {
  if (icon === 'claim') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
      >
        <path
          d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (icon === 'done') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
      >
        <path
          d="M5 13L9 17L19 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // reviewed
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className="w-6 h-6"
    >
      <path
        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const ChatItem = ({
  id,
  name,
  orderTitle,
  orderStatus,
  lastMessage,
  time,
  avatar,
  unreadCount = 0,
  isSelected,
  onClick,
}: ChatItemProps) => {
  const statusBadge = orderStatus ? getStatusBadgeConfig(orderStatus) : null;

  return (
    <div
      onClick={() => onClick(id)}
      className={cn(
        'relative flex items-center gap-5 p-2.5 cursor-pointer transition-colors',
        'border-t border-accent-secondary',
        isSelected
          ? 'bg-background-secondary'
          : 'hover:bg-background-secondary/30'
      )}
    >
      {/* Avatar - 80x80 per design */}
      <Avatar
        src={avatar}
        alt={name}
        size="xl"
        fallback={name.charAt(0)}
        className="w-20 h-20 flex-shrink-0"
      />

      {/* Chat Info */}
      <div className="flex-1 min-w-0 flex flex-col gap-[5px]">
        <div className="flex items-start justify-between">
          <div className="flex-1 flex flex-col gap-2.5 min-w-0">
            {/* Name - Secondary text color */}
            <span className="text-text-secondary text-xl font-medium font-['Roboto'] leading-6 truncate">
              {name}
            </span>

            {/* Order Title - Primary text, bold */}
            {orderTitle && (
              <span className="text-text-primary text-xl font-medium font-['Roboto'] leading-6 truncate">
                {orderTitle}
              </span>
            )}

            {/* Last Message */}
            <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5 line-clamp-1">
              {lastMessage}
            </span>
          </div>

          {/* Time */}
          <span className="text-text-secondary text-base font-normal font-['Roboto'] leading-5 flex-shrink-0 ml-4">
            {time}
          </span>
        </div>
      </div>

      {/* Status Badge - positioned at bottom right */}
      {statusBadge && (
        <div
          className={cn(
            'absolute right-2.5 bottom-2.5',
            'h-9 px-3.5 py-3 rounded-[10px]',
            'flex items-center gap-[5px]',
            statusBadge.bgColor,
            statusBadge.textColor
          )}
        >
          {statusBadge.icon && <StatusIcon icon={statusBadge.icon} />}
          <span className="text-xl font-medium font-['Roboto'] leading-6">
            {statusBadge.label}
          </span>
        </div>
      )}

      {/* Unread Badge - show only if no status badge */}
      {!statusBadge && unreadCount > 0 && (
        <div className="absolute right-2.5 bottom-2.5 bg-accent-primary text-white rounded-full px-2 py-0.5 text-xs font-medium min-w-[20px] text-center">
          {unreadCount > 99 ? '99+' : unreadCount}
        </div>
      )}
    </div>
  );
};
